/**
 * Conteúdo da Ajuda do Elos.
 *
 * As três primeiras lições valem para todo mundo (membro comum). As outras são
 * mostradas conforme o papel da pessoa: quem tem convites e funções, quem lidera
 * um ministério, quem é Gestor de elo e, no grupo "Seus elos", uma lição curta
 * para cada elo que ela pode cuidar (Escala, Biblioteca, Eventos, Reservas,
 * Comunicados e Visitantes). Aprovado em 02/11/2026 (protótipo "Ajuda: ministérios,
 * funções e gestão", v2).
 *
 * Arquivo SÓ de dados e regras puras (sem React, sem rede): assim dá para
 * testar com `node` + esbuild, como as outras regras do projeto.
 *
 * A Ajuda é uma camada de orientação. Ela NUNCA cria, concede ou amplia
 * permissão: só decide QUAIS passos mostrar a partir de `modulosVisiveis`
 * (a mesma lista que a Central Elos usa) e do `PapelAjuda` (quem lidera, quem
 * gerencia, quais elos). O banco (RLS) continua sendo o portão real.
 *
 * Marcação de texto: `**trecho**` vira negrito na tela.
 */

/** Ilustrações das três lições para todos (as da v1). */
export const ILUSTRACOES_BASE = [
  "abas",
  "central",
  "menu",
  "voltar",
  "nao-achou",
  "sino",
  "painel",
  "preferencias",
  "aviso-comunicado",
  "escala-cartao",
  "escala-responder",
  "calendario",
] as const;

/** Ilustrações das lições de ministério, liderança e Gestor de elo. */
export const ILUSTRACOES_LIDERANCA = [
  "convite",
  "convite-acao",
  "funcoes",
  "avisos-funcao",
  "meu-ministerio",
  "selo-lider",
  "nova-funcao",
  "convidar",
  "dar-funcao",
  "aviso-convite-resposta",
  "elo",
  "comparar-lider-gestor",
  "selo-gestor",
  "cartao-gestor",
  "nova-funcao-gestor",
  "elo-quem",
  "lista-elos",
] as const;

/** Ilustrações das lições de cada elo. */
export const ILUSTRACOES_ELO = [
  "poderes-escala",
  "escala-nova",
  "escala-equipe",
  "escala-menu",
  "poderes-biblioteca",
  "bib-livro",
  "bib-pedido",
  "bib-devolucao",
  "poderes-eventos",
  "ev-novo",
  "ev-aprovar",
  "ev-ambiente",
  "poderes-reservas",
  "res-nova",
  "res-aprovar",
  "res-ambientes",
  "poderes-comunicados",
  "com-novo",
  "com-prazo",
  "com-acoes",
  "poderes-visitantes",
  "vis-novo",
  "vis-avaliar",
  "vis-privacidade",
] as const;

export type IlustracaoBaseId = (typeof ILUSTRACOES_BASE)[number];
export type IlustracaoLiderancaId = (typeof ILUSTRACOES_LIDERANCA)[number];
export type IlustracaoEloId = (typeof ILUSTRACOES_ELO)[number];
export type IlustracaoId = IlustracaoBaseId | IlustracaoLiderancaId | IlustracaoEloId;

/**
 * O papel da pessoa, só para decidir QUAIS lições mostrar. Quem calcula é o hook
 * `useAjudaPapel`, a partir das mesmas regras do banco; aqui é só leitura.
 */
export interface PapelAjuda {
  /** Lidera ao menos um ministério ativo. */
  lider: boolean;
  /** Tem função de Gestor de elo em ao menos um ministério ativo. */
  gestor: boolean;
  /** Códigos dos elos que a pessoa gerencia (ex.: "biblioteca"). */
  elos: string[];
}

/** Membro comum: sem liderança, sem gestão. É o padrão enquanto o papel carrega. */
export const PAPEL_MEMBRO: PapelAjuda = { lider: false, gestor: false, elos: [] };

/** Lidera um ministério ou cuida de um elo (líder ou Gestor de elo). */
export function ehEquipe(papel: PapelAjuda): boolean {
  return papel.lider || papel.gestor || papel.elos.length > 0;
}

/** Gerencia este elo (a mesma regra `gerencia_modulo` do banco). */
export function gerenciaElo(papel: PapelAjuda, codigo: string): boolean {
  return papel.elos.includes(codigo);
}

/** Ids das três lições para todos: são as que o cartão "Novo por aqui?" acompanha. */
export const LICOES_BASE = ["conhecendo", "avisos", "atividades"] as const;

export interface PassoAjuda {
  titulo: string;
  ilustracao: IlustracaoId;
  /** Parágrafos curtos. Aceita `**negrito**`. */
  texto: string[];
  /** Condição ou limite que o membro precisa saber (ex.: depende da igreja). */
  depende?: string;
  /** Dica curta, em destaque. */
  dica?: string;
  /** O passo só aparece se este elo estiver visível para a pessoa. */
  requerModulo?: string;
  /** O passo só aparece se este elo NÃO estiver visível para a pessoa. */
  semModulo?: string;
}

/** O que um texto "por papel" recebe para decidir as frases. */
interface ContextoTexto {
  papel: PapelAjuda;
  /** Nomes dos elos que têm lição própria para esta pessoa (ex.: "Escala"). */
  nomesDasLicoesDeElo: string[];
}

interface PassoDef extends Omit<PassoAjuda, "texto"> {
  texto: string[] | ((c: ContextoTexto) => string[]);
  /** O passo só aparece se esta regra de papel disser que sim. */
  somenteSe?: (papel: PapelAjuda) => boolean;
}

interface LicaoDef {
  id: string;
  titulo: string;
  resumo: string;
  /** Nota curta da lista (ex.: "Para quem lidera um ministério"). */
  nota?: string | ((modulosVisiveis: string[]) => string);
  /** A lição só vale para quem passa nesta regra de papel. Sem isso, vale para todos. */
  visivel?: (papel: PapelAjuda) => boolean;
  /** A lição só aparece se este elo estiver visível para a pessoa. */
  requerModulo?: string;
  /** Título do grupo, na lista de lições (ex.: "Seus elos"). */
  grupo?: string;
  /** Nome curto do elo, para a frase "As suas lições: …". Só nas lições de elo. */
  nomeElo?: string;
  passos: PassoDef[];
}

export interface LicaoMontada {
  id: string;
  titulo: string;
  resumo: string;
  passos: PassoAjuda[];
  /** Nota curta para a lista de lições (ex.: "Depende do seu ministério"). */
  nota: string | null;
  /** Título do grupo a que a lição pertence na lista (ex.: "Seus elos"), ou `null`. */
  grupo: string | null;
}

const lider = (p: PapelAjuda) => p.lider;
const gerencia = (codigo: string) => (p: PapelAjuda) => gerenciaElo(p, codigo);

const GRUPO_ELOS = "Seus elos";

const LICOES: LicaoDef[] = [
  {
    id: "conhecendo",
    titulo: "Conhecendo o Elos",
    resumo: "Onde fica cada coisa",
    passos: [
      {
        titulo: "Os lugares principais, embaixo da tela",
        ilustracao: "abas",
        texto: [
          "Embaixo da tela ficam os lugares principais.",
          "**Início:** um resumo do que é seu.\n**Calendário:** as datas do que você pode ver.\n**Central Elos:** a entrada para cada área.",
        ],
      },
      {
        titulo: "Central Elos: a lista das áreas",
        ilustracao: "central",
        texto: [
          "Cada área do Elos se chama **elo**.",
          "Na Central Elos você vê os elos que a sua igreja liberou para você.",
        ],
        depende: "Cada igreja libera os seus elos",
      },
      {
        titulo: "O botão de menu",
        ilustracao: "menu",
        texto: [
          "No canto de cima, à direita, fica o botão de menu.",
          "Nele estão o seu perfil, o tema claro ou escuro, o **Sair** e esta **Ajuda do Elos**.",
        ],
        dica: "Para rever estas lições quando quiser, abra o menu e toque em **Ajuda do Elos**.",
      },
      {
        titulo: "Como voltar",
        ilustracao: "voltar",
        texto: [
          "Dentro de uma área, use a seta no canto de cima, à esquerda.",
          "Ela leva você para a tela anterior.",
        ],
        dica: "Ficou perdido? Toque em **Início**, na barra de baixo.",
      },
      {
        titulo: "Não achou uma opção?",
        ilustracao: "nao-achou",
        texto: [
          "Nem todo mundo vê as mesmas áreas.",
          "Isso depende da sua igreja e do seu ministério.",
          "Se precisar de algo que não vê, **peça a quem lidera o seu ministério**.",
        ],
        dica: "Não é erro seu. E não precisa tentar dar um jeito.",
      },
    ],
  },
  {
    id: "avisos",
    titulo: "Avisos e notificações",
    resumo: "O sino e as mensagens",
    passos: [
      {
        titulo: "O sino",
        ilustracao: "sino",
        texto: [
          "Nas telas principais, o sino fica no canto de cima, à esquerda.",
          "Uma **bolinha vermelha** no sino quer dizer que há novidade, como um aviso novo ou um convite.",
        ],
      },
      {
        titulo: "Lendo um aviso",
        ilustracao: "painel",
        texto: [
          "Toque no sino para abrir a lista de avisos.",
          "Toque em um aviso para abrir o assunto dele. Os avisos já lidos ficam mais claros.",
        ],
        dica: "Já leu tudo? Toque em **Marcar todas como lidas**.",
      },
      {
        titulo: "Escolha o que receber",
        ilustracao: "preferencias",
        texto: [
          "No alto da lista de avisos fica a engrenagem **Configurar notificações**.",
          "Lá você escolhe quais avisos quer receber. Também escolhe se quer recebê-los neste aparelho.",
        ],
        depende: "Avisos no aparelho pedem a sua permissão",
      },
      {
        titulo: "Aviso ou comunicado?",
        ilustracao: "aviso-comunicado",
        texto: [
          "**Aviso:** uma mensagem curta, que aparece no sino.",
          "**Comunicado:** uma mensagem completa da igreja, na área Comunicados.",
        ],
        depende: "Comunicados só aparecem se a igreja liberou esse elo para você",
        requerModulo: "comunicados",
      },
    ],
  },
  {
    id: "atividades",
    titulo: "Minhas atividades",
    resumo: "Escala e suas datas",
    nota: (modulosVisiveis) =>
      modulosVisiveis.includes("escala")
        ? "Depende do seu ministério"
        : "A Escala não está liberada para você",
    passos: [
      {
        titulo: "Escala: onde você serve",
        ilustracao: "escala-cartao",
        texto: [
          "Na **Escala** você vê onde foi escalado para servir.",
          "Em **Próximas** ficam as datas que vêm aí. Em **Anteriores** e **Canceladas** fica o resto.",
        ],
        depende: "Aparece para quem participa de um ministério que usa a Escala",
        requerModulo: "escala",
      },
      {
        titulo: "Responda ao convite",
        ilustracao: "escala-responder",
        texto: [
          "Quando você é escalado, o cartão mostra **Aguardando sua resposta**.",
          "Abra a escala e toque em **Responder**. Escolha **Confirmar presença** ou **Recusar escala**.",
        ],
        dica: "Ao recusar, você escreve o motivo. A liderança do ministério é avisada.",
        requerModulo: "escala",
      },
      {
        titulo: "Sem Escala por aqui",
        ilustracao: "nao-achou",
        texto: [
          "A **Escala** não aparece para você agora.",
          "Isso é normal. Ela depende da sua igreja e do seu ministério.",
        ],
        depende: "Não é um defeito do Elos",
        semModulo: "escala",
      },
      {
        titulo: "Suas datas, no Calendário",
        ilustracao: "calendario",
        texto: [
          "O **Calendário** junta as suas datas num lugar só.",
          "A cor de cada tipo mostra do que se trata. Toque num tipo para esconder ou mostrar.",
        ],
        depende: "Só aparece o que você pode ver",
      },
    ],
  },

  /* ---------- Ministério, liderança e Gestor de elo ---------- */
  {
    id: "ministerio",
    titulo: "Meu ministério",
    resumo: "Convites e funções",
    passos: [
      {
        titulo: "Você pode ser convidado",
        ilustracao: "convite",
        texto: [
          "Quem lidera um ministério pode convidar você para ele.",
          "O convite aparece no **Início**, no **sino** e como aviso no celular.",
        ],
        dica: "Convite não é obrigação. A decisão é sua.",
      },
      {
        titulo: "Aceitar ou recusar",
        ilustracao: "convite-acao",
        texto: [
          "Toque em **Aceitar** para entrar no ministério com aquela função.",
          "Toque em **Recusar** se não quiser. O Elos pergunta de novo antes de recusar.",
        ],
        depende: "Enquanto você não responder, você ainda não faz parte",
      },
      {
        titulo: "O que é uma função",
        ilustracao: "funcoes",
        texto: [
          "Dentro de um ministério, cada pessoa tem uma **função**, como Membro ou Secretário.",
          "Uma pessoa pode ter mais de uma função.",
        ],
      },
      {
        titulo: "Quando a função chega",
        ilustracao: "avisos-funcao",
        texto: [
          "Se você já faz parte do ministério, a liderança pode dar uma função comum a você. Ela vale na hora, e você recebe um aviso.",
          "As funções de **Líder do Ministério** e de **Gestor de elo** sempre pedem o seu aceite.",
        ],
        dica: "Se uma função for retirada, ou você sair do ministério, você também recebe um aviso.",
      },
    ],
  },
  {
    id: "liderar",
    titulo: "Liderar um ministério",
    resumo: "Funções e convites",
    nota: "Para quem lidera um ministério",
    visivel: lider,
    passos: [
      {
        titulo: "Seu ministério fica no Início",
        ilustracao: "meu-ministerio",
        texto: [
          "Quem lidera um ministério vê o cartão **Meu ministério** no Início.",
          "Toque em **Gerenciar** para abrir a tela do ministério.",
        ],
        depende: "Aparece só para quem lidera um ministério",
      },
      {
        titulo: "O Líder do Ministério",
        ilustracao: "selo-lider",
        texto: [
          "O **Líder do Ministério** conduz o ministério: convida e remove integrantes e define as funções.",
          "O selo **Líder do Ministério** aparece ao lado do nome de quem tem essa função.",
        ],
        dica: "Dê essa função só a quem realmente lidera.",
      },
      {
        titulo: "1º passo: crie a função",
        ilustracao: "nova-funcao",
        texto: [
          "A função precisa existir no ministério antes de você convidar alguém.",
          "Toque em **+ Nova função**, escreva o nome e toque em **Criar função**.",
          "Marque **Lidera o ministério** ou **Gestor de elo** só se a função for de liderança ou de gestão.",
        ],
        dica: "A função de Líder do Ministério normalmente já vem pronta em cada ministério.",
      },
      {
        titulo: "2º passo: convide a pessoa",
        ilustracao: "convidar",
        texto: [
          "Na lista **Integrantes**, toque em **Convidar**. Escolha a função e a pessoa.",
          "Quem é convidado fica com **Convite pendente** até aceitar.",
        ],
        depende: "Para entrar no ministério, a pessoa sempre precisa aceitar o convite",
        dica: "Quer desfazer? Cancele o convite no botão ao lado do nome.",
      },
      {
        titulo: "Quem já é do ministério",
        ilustracao: "dar-funcao",
        texto: [
          "Para dar outra função a quem já é integrante, toque em **+ Função**, ao lado do nome.",
          "Função comum: **Entra na hora**, e a pessoa é avisada.",
          "**Líder do Ministério** e **Gestor de elo**: **Pede aceite**.",
        ],
      },
      {
        titulo: "Você é avisado das respostas",
        ilustracao: "aviso-convite-resposta",
        texto: ["Quando a pessoa aceita ou recusa o convite, você recebe um aviso no sino e no celular."],
      },
    ],
  },
  {
    id: "gestor",
    titulo: "Gestor de elo",
    resumo: "Cuidar de uma área",
    nota: "Para quem gerencia um elo",
    visivel: ehEquipe,
    passos: [
      {
        titulo: "O que é um elo",
        ilustracao: "elo",
        texto: [
          "Cada área do Elos se chama **elo**, como a **Biblioteca**.",
          "Cada elo tem um **ministério responsável**.",
        ],
      },
      {
        titulo: "Líder e Gestor de elo",
        ilustracao: "comparar-lider-gestor",
        texto: [
          "O **Líder do Ministério** conduz o ministério e também gerencia os elos dele.",
          "O **Gestor de elo** ajuda a cuidar dos elos, mas não convida nem remove integrantes.",
        ],
        dica: "Cuidar de um elo: o líder e o gestor podem as mesmas coisas.",
      },
      {
        titulo: "O selo do Gestor de elo",
        ilustracao: "selo-gestor",
        texto: [
          "Quem tem a função **Gestor de elo** ganha o selo **Gestor de elo**, com desenho próprio.",
          "Quem lidera o ministério vê o selo ao lado do nome, na lista de integrantes, igual ao selo do Líder do Ministério.",
        ],
      },
      {
        titulo: "Seu cartão no Início",
        ilustracao: "cartao-gestor",
        texto: [
          "No **Início**, o cartão **Elos que você gerencia** mostra os seus elos.",
          "Toque em **Ver como usar** para abrir as lições deles.",
        ],
        depende: "Aparece só para quem tem a função Gestor de elo",
      },
      {
        titulo: "Como alguém vira Gestor de elo",
        ilustracao: "nova-funcao-gestor",
        texto: [
          "Ao criar a função, marque **Gestor de elo**.",
          "Depois, convide a pessoa. Ela só vira Gestora de elo quando aceitar.",
        ],
        somenteSe: lider,
      },
      {
        titulo: "Veja quem gerencia",
        ilustracao: "elo-quem",
        texto: [
          "Na tela do ministério, o cartão **Elo sob responsabilidade** mostra **quem gerencia**.",
          "Se não houver ninguém, aparece **Ninguém gerencia ainda**.",
        ],
        somenteSe: lider,
      },
      {
        titulo: "O que fazer em cada elo",
        ilustracao: "lista-elos",
        texto: ({ nomesDasLicoesDeElo }) => [
          "Cada elo tem uma lição própria na Ajuda. Ela aparece só para quem pode usar aquele elo.",
          nomesDasLicoesDeElo.length
            ? `As suas lições: ${nomesDasLicoesDeElo.map((n) => `**${n}**`).join(", ")}.`
            : "Quando você passar a cuidar de um elo, a lição dele aparece aqui.",
        ],
      },
    ],
  },

  /* ---------- Uma lição por elo ---------- */
  {
    id: "escala",
    titulo: "Escala: criar e acompanhar",
    resumo: "Para quem cria escala",
    nota: "Para quem lidera ou gerencia um ministério",
    grupo: GRUPO_ELOS,
    nomeElo: "Escala",
    requerModulo: "escala",
    visivel: ehEquipe,
    passos: [
      {
        titulo: "O que você faz na Escala",
        ilustracao: "poderes-escala",
        texto: [
          "Quem lidera ou é Gestor de elo de um ministério cria as escalas dele.",
          "Cada escala pertence a **um ministério**.",
        ],
      },
      {
        titulo: "Criar a escala",
        ilustracao: "escala-nova",
        texto: [
          "Toque em **Nova escala** e escolha o ministério, a data e o horário.",
          "Ligue **Exigir confirmação dos participantes** se cada pessoa precisa confirmar a presença.",
        ],
        dica: "O ministério e a confirmação não mudam depois que a escala é criada.",
      },
      {
        titulo: "Escalar as pessoas",
        ilustracao: "escala-equipe",
        texto: [
          "Na escala, toque em **Escalar participantes** e escolha cada pessoa e a função dela.",
          "Se alguém já está escalado em outro lugar na mesma hora, o Elos avisa. Você ainda pode escalar.",
        ],
        depende: "Quem é escalado recebe um aviso",
      },
      {
        titulo: "Ajustar e cancelar",
        ilustracao: "escala-menu",
        texto: [
          "Cada pessoa tem um menu **⋯**: **Alterar função**, **Registrar resposta** e **Remover da escala**.",
          "Use **Registrar resposta** quando a pessoa respondeu fora do Elos.",
          "A escala toda pode ser **editada** ou **cancelada**. O **Histórico** guarda o que mudou.",
        ],
      },
    ],
  },
  {
    id: "biblioteca",
    titulo: "Biblioteca: acervo e empréstimos",
    resumo: "Para quem cuida da Biblioteca",
    nota: "Para quem gerencia a Biblioteca",
    grupo: GRUPO_ELOS,
    nomeElo: "Biblioteca",
    requerModulo: "biblioteca",
    visivel: gerencia("biblioteca"),
    passos: [
      {
        titulo: "O que você faz na Biblioteca",
        ilustracao: "poderes-biblioteca",
        texto: [
          "Quem lidera ou é Gestor de elo do ministério da Biblioteca cuida do **acervo** e dos **empréstimos**.",
        ],
      },
      {
        titulo: "Cadastrar um livro",
        ilustracao: "bib-livro",
        texto: [
          "Em **Acervo de livros**, toque em **+ Novo livro**.",
          "Cada livro tem um **número de cadastro**.",
        ],
        dica: "Use **+ Nova categoria** para organizar os livros.",
      },
      {
        titulo: "Pedidos de empréstimo",
        ilustracao: "bib-pedido",
        texto: ["Em **Empréstimos**, veja os pedidos.", "Toque em **Aprovar** ou em **Recusar**."],
      },
      {
        titulo: "Registrar a devolução",
        ilustracao: "bib-devolucao",
        texto: [
          "Quando o livro voltar, toque em **Registrar devolução**.",
          "A equipe da Biblioteca recebe um aviso. Quem registrou e quem devolveu não recebem.",
        ],
      },
    ],
  },
  {
    id: "eventos",
    titulo: "Eventos: criar e aprovar",
    resumo: "Para quem organiza eventos",
    nota: "Para quem lidera ou gerencia",
    grupo: GRUPO_ELOS,
    nomeElo: "Eventos",
    requerModulo: "eventos",
    visivel: ehEquipe,
    passos: [
      {
        titulo: "O que você faz em Eventos",
        ilustracao: "poderes-eventos",
        texto: ({ papel }) =>
          gerenciaElo(papel, "eventos")
            ? [
                "Quem gerencia Eventos cria, aprova e cancela os eventos da igreja.",
                "Os outros ministérios **pedem** o evento, e você decide.",
              ]
            : [
                "Você pode **pedir** um evento para o seu ministério.",
                "Quem gerencia o elo Eventos aprova ou recusa.",
              ],
      },
      {
        titulo: "Criar ou pedir um evento",
        ilustracao: "ev-novo",
        texto: [
          "Toque em **+ Novo evento** e preencha os dados.",
          "Se quem criou não gerencia Eventos, o evento fica **Pendente** até alguém aprovar.",
        ],
      },
      {
        titulo: "Aprovar ou recusar",
        ilustracao: "ev-aprovar",
        texto: [
          "Os eventos **Pendentes** aparecem com **Aprovar** e **Recusar**.",
          "Ao recusar, escreva o **motivo**.",
        ],
        somenteSe: gerencia("eventos"),
      },
      {
        titulo: "Ambiente e cancelamento",
        ilustracao: "ev-ambiente",
        texto: [
          "Se o evento usa um ambiente, o Elos **reserva sozinho** e confere os conflitos.",
          "Para cancelar, toque em **Cancelar evento** e escreva o **motivo**.",
        ],
      },
    ],
  },
  {
    id: "reservas",
    titulo: "Reservas: pedir e aprovar",
    resumo: "Para quem cuida das reservas",
    nota: "Para quem lidera ou gerencia",
    grupo: GRUPO_ELOS,
    nomeElo: "Reservas",
    requerModulo: "reservas",
    visivel: ehEquipe,
    passos: [
      {
        titulo: "O que você faz em Reservas",
        ilustracao: "poderes-reservas",
        texto: ({ papel }) =>
          gerenciaElo(papel, "reservas")
            ? ["Quem gerencia Reservas decide os pedidos de salas e ambientes e cuida dos ambientes."]
            : [
                "Você pode **pedir** uma sala ou ambiente para o seu ministério.",
                "Quem gerencia o elo Reservas decide.",
              ],
      },
      {
        titulo: "Pedir uma reserva",
        ilustracao: "res-nova",
        texto: [
          "Toque em **+ Nova reserva** e escolha o ambiente, o dia e o horário.",
          "O Elos confere se o ambiente já está ocupado.",
        ],
      },
      {
        titulo: "Aprovar ou recusar",
        ilustracao: "res-aprovar",
        texto: [
          "Os pedidos **Pendentes** aparecem com **Aprovar** e **Recusar**.",
          "Ao recusar ou cancelar, escreva o **motivo**.",
        ],
        somenteSe: gerencia("reservas"),
      },
      {
        titulo: "Cuidar dos ambientes",
        ilustracao: "res-ambientes",
        texto: ["Em **Ambientes**, toque em **+ Novo ambiente** para cadastrar uma sala ou espaço."],
        somenteSe: gerencia("reservas"),
      },
    ],
  },
  {
    id: "comunicados",
    titulo: "Comunicados: avisar a igreja",
    resumo: "Para quem publica avisos",
    nota: "Para quem gerencia Comunicados",
    grupo: GRUPO_ELOS,
    nomeElo: "Comunicados",
    requerModulo: "comunicados",
    visivel: gerencia("comunicados"),
    passos: [
      {
        titulo: "O que você faz em Comunicados",
        ilustracao: "poderes-comunicados",
        texto: [
          "Só quem lidera ou gerencia o elo **Comunicados** cria e edita os avisos da igreja.",
        ],
      },
      {
        titulo: "Escrever e publicar",
        ilustracao: "com-novo",
        texto: [
          "Toque em **Novo comunicado**, escreva o aviso e toque em **Publicar**.",
        ],
      },
      {
        titulo: "Apagar sozinho em 30 dias",
        ilustracao: "com-prazo",
        texto: [
          "Ligue **Apagar sozinho em 30 dias** para o comunicado sumir depois desse prazo.",
          "Se você editar, a contagem **não recomeça**.",
        ],
      },
      {
        titulo: "Arquivar, restaurar e apagar",
        ilustracao: "com-acoes",
        texto: [
          "**Arquivar** tira o comunicado da vista dos membros. **Restaurar** traz de volta.",
          "**Apagar** apaga para todos e **não tem volta**.",
        ],
        dica: "Na dúvida, arquive. Arquivado não se perde.",
      },
    ],
  },
  {
    id: "visitantes",
    titulo: "Visitantes: acompanhar quem chega",
    resumo: "Para quem acompanha visitantes",
    nota: "Para quem lidera ou gerencia",
    grupo: GRUPO_ELOS,
    nomeElo: "Visitantes",
    requerModulo: "visitantes",
    visivel: ehEquipe,
    passos: [
      {
        titulo: "O que você faz em Visitantes",
        ilustracao: "poderes-visitantes",
        texto: ({ papel }) =>
          gerenciaElo(papel, "visitantes")
            ? ["Quem gerencia Visitantes cuida do cadastro, das visitas e dos contatos, e vê todos os dados."]
            : [
                "Líderes e gestores informam novos visitantes e registram o contato feito.",
                "Você vê só o básico. O resto fica com a equipe do elo.",
              ],
      },
      {
        titulo: "Cadastrar ou informar",
        ilustracao: "vis-novo",
        texto: ({ papel }) =>
          gerenciaElo(papel, "visitantes")
            ? ["Toque em **Novo** para cadastrar um visitante."]
            : [
                "Toque em **Novo** para informar um visitante.",
                "A equipe do elo avalia o que você informou.",
              ],
      },
      {
        titulo: "Avaliar quem foi informado",
        ilustracao: "vis-avaliar",
        texto: ["Na aba **Visitantes pendentes**, avalie quem outros líderes informaram."],
        somenteSe: gerencia("visitantes"),
      },
      {
        titulo: "Cuide dos dados",
        ilustracao: "vis-privacidade",
        texto: [
          "Os dados dos visitantes são **sensíveis**. Use só para cuidar da pessoa na igreja.",
          "Líderes de outros ministérios veem menos que o ministério responsável.",
        ],
      },
    ],
  },
];

function passoVale(p: PassoDef, modulosVisiveis: string[], papel: PapelAjuda): boolean {
  if (p.requerModulo && !modulosVisiveis.includes(p.requerModulo)) return false;
  if (p.semModulo && modulosVisiveis.includes(p.semModulo)) return false;
  if (p.somenteSe && !p.somenteSe(papel)) return false;
  return true;
}

function licaoVale(l: LicaoDef, modulosVisiveis: string[], papel: PapelAjuda): boolean {
  if (l.requerModulo && !modulosVisiveis.includes(l.requerModulo)) return false;
  if (l.visivel && !l.visivel(papel)) return false;
  return true;
}

function resolverPasso(p: PassoDef, contexto: ContextoTexto): PassoAjuda {
  const { somenteSe: _somenteSe, texto, ...resto } = p;
  return { ...resto, texto: typeof texto === "function" ? texto(contexto) : texto };
}

/** Passos que valem para esta pessoa, dado o que o Elos liberou para ela e o papel dela. */
function passosVisiveis(
  licao: LicaoDef,
  modulosVisiveis: string[],
  papel: PapelAjuda = PAPEL_MEMBRO,
): PassoAjuda[] {
  const contexto: ContextoTexto = { papel, nomesDasLicoesDeElo: nomesDasLicoesDeElo(modulosVisiveis, papel) };
  return licao.passos.filter((p) => passoVale(p, modulosVisiveis, papel)).map((p) => resolverPasso(p, contexto));
}

/** Nomes dos elos que têm lição própria para esta pessoa (a frase "As suas lições"). */
function nomesDasLicoesDeElo(modulosVisiveis: string[], papel: PapelAjuda): string[] {
  return LICOES.filter((l) => l.nomeElo && licaoVale(l, modulosVisiveis, papel)).map(
    (l) => l.nomeElo as string,
  );
}

/** Nota da lista de lições. */
function notaDaLicao(l: LicaoDef, modulosVisiveis: string[]): string | null {
  if (!l.nota) return null;
  return typeof l.nota === "function" ? l.nota(modulosVisiveis) : l.nota;
}

export function montarLicoes(
  modulosVisiveis: string[],
  papel: PapelAjuda = PAPEL_MEMBRO,
): LicaoMontada[] {
  return LICOES.filter((l) => licaoVale(l, modulosVisiveis, papel))
    .map((l) => ({
      id: l.id,
      titulo: l.titulo,
      resumo: l.resumo,
      passos: passosVisiveis(l, modulosVisiveis, papel),
      nota: notaDaLicao(l, modulosVisiveis),
      grupo: l.grupo ?? null,
    }))
    .filter((l) => l.passos.length > 0);
}

/**
 * Qual lição nova o Início oferece em um cartão ("Novo para quem lidera" ou "Novo para quem
 * gerencia um elo"): a de quem lidera vem primeiro; some quando a lição foi vista ou o cartão
 * foi fechado com "Agora não". Só uma por vez. `null` quando não há nada a oferecer.
 */
export function licaoNovaParaInicio(
  papel: PapelAjuda,
  vistas: string[],
  dispensadas: string[],
): "liderar" | "gestor" | null {
  const livre = (id: string) => !vistas.includes(id) && !dispensadas.includes(id);
  if (papel.lider && livre("liderar")) return "liderar";
  if (papel.gestor && livre("gestor")) return "gestor";
  return null;
}

export type Destino = { tipo: "licao"; licaoId: string; passo: number } | { tipo: "fim" };

/** Normaliza `?passo=` (1 a n) para índice 0-based; inválido cai no primeiro. */
export function indicePasso(bruto: string | null, total: number): number {
  const n = Number.parseInt(bruto ?? "", 10);
  if (!Number.isFinite(n) || n < 1 || n > total) return 0;
  return n - 1;
}

/** Para onde vai o "Próximo": o passo seguinte, a próxima lição ou o fim. */
export function proximoDestino(licoes: LicaoMontada[], licaoId: string, passo: number): Destino {
  const i = licoes.findIndex((l) => l.id === licaoId);
  const atual = licoes[i];
  if (!atual) return { tipo: "fim" };
  if (passo < atual.passos.length - 1) return { tipo: "licao", licaoId, passo: passo + 1 };
  const proxima = licoes[i + 1];
  return proxima ? { tipo: "licao", licaoId: proxima.id, passo: 0 } : { tipo: "fim" };
}

/** Para onde vai o "Anterior"; `null` no primeiro passo da primeira lição. */
export function destinoAnterior(
  licoes: LicaoMontada[],
  licaoId: string,
  passo: number,
): Destino | null {
  const i = licoes.findIndex((l) => l.id === licaoId);
  if (i < 0) return null;
  if (passo > 0) return { tipo: "licao", licaoId, passo: passo - 1 };
  const anterior = licoes[i - 1];
  if (!anterior) return null;
  return { tipo: "licao", licaoId: anterior.id, passo: Math.max(anterior.passos.length - 1, 0) };
}

/** Quebra `**negrito**` em partes. Sem HTML: o React escapa tudo. */
export function partesDoTexto(texto: string): { texto: string; negrito: boolean }[] {
  return texto
    .split("**")
    .map((trecho, i) => ({ texto: trecho, negrito: i % 2 === 1 }))
    .filter((p) => p.texto.length > 0);
}
